import { Injectable } from '@angular/core';

import { Logger } from "../utils/logger";

@Injectable({
  providedIn: 'root'
})
export class SignalingService extends EventTarget {
  private running: boolean = false;
  private interval: number = 1000;
  private sessionId?: string;

  constructor() {
    super();
    
  }

  private sleep(msec: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, msec));
  }

  private headers(): { 'Content-Type': string, 'Session-Id'?: string } {
    return this.sessionId !== undefined
      ? { 'Content-Type': 'application/json', 'Session-Id': this.sessionId }
      : { 'Content-Type': 'application/json' };
  }

  private url(method: string = '', parameter: string = ''): string {
    let ret = `${location.origin}/signaling`;
    if (method) ret += `/${method}`;
    if (parameter) ret += `?${parameter}`;
    return ret;
  }

  async start(interval?: number): Promise<void> {
    if (interval) this.interval = interval;
    if (this.running) return;

    this.running = true;
    while (!this.sessionId) {
      const createResponse = await fetch(this.url(''), { method: 'PUT', headers: this.headers() });
      const session = await createResponse.json();
      this.sessionId = session.sessionId;

      if (!this.sessionId) {
        await this.sleep(this.interval);
      }
    }

    this.loopGetAll();
  }

  private async loopGetAll(): Promise<void> {
    let lastTimeRequest = Date.now() - 30000;
    while (this.running) {
      const res = await this.getAll(lastTimeRequest);
      const data = await res.json();
      lastTimeRequest = data.datetime ? data.datetime : Date.now();

      const messages = data.messages;

      for (const msg of messages) {
        switch (msg.type) {
          case 'connect':
            break;
          case 'disconnect':
            this.dispatchEvent(new CustomEvent('disconnect', { detail: msg }));
            break;
          case 'offer':
            this.dispatchEvent(new CustomEvent('offer', { detail: msg }));
            break;
          case 'answer':
            this.dispatchEvent(new CustomEvent('answer', { detail: msg }));
            break;
          case 'candidate':
            this.dispatchEvent(new CustomEvent('candidate', { detail: msg }));
            break;
          default:
            break;
        }
      }
      await this.sleep(this.interval);
    }
  }

  async stop(): Promise<void> {
    this.running = false;
    await fetch(this.url(''), { method: 'DELETE', headers: this.headers() });
    this.sessionId = undefined;
  }

  async createConnection(connectionId: string): Promise<any> {
    const data = { connectionId };
    const res = await fetch(this.url('connection'), { method: 'PUT', headers: this.headers(), body: JSON.stringify(data) });
    const json = await res.json();
    Logger.log(`Signaling: HTTP create connection, connectionId: ${json.connectionId}, polite: ${json.polite}`);

    this.dispatchEvent(new CustomEvent('connect', { detail: json }));
    return json;
  }

  async deleteConnection(connectionId: string): Promise<any> {
    const data = { connectionId };
    const res = await fetch(this.url('connection'), { method: 'DELETE', headers: this.headers(), body: JSON.stringify(data) });
    const json = await res.json();
    this.dispatchEvent(new CustomEvent('disconnect', { detail: json }));
    return json;
  }

  async sendOffer(connectionId: string, sdp: string): Promise<void> {
    const data = { sdp, connectionId };
    Logger.log('sendOffer:' + data);
    await fetch(this.url('offer'), { method: 'POST', headers: this.headers(), body: JSON.stringify(data) });
  }

  async sendAnswer(connectionId: string, sdp: string): Promise<void> {
    const data = { sdp, connectionId };
    Logger.log('sendAnswer:' + data);
    await fetch(this.url('answer'), { method: 'POST', headers: this.headers(), body: JSON.stringify(data) });
  }

  sendCandidate(connectionId: string, candidate: string, sdpMid: any, sdpMLineIndex: number): Promise<any> {
      const data = { candidate, sdpMLineIndex, sdpMid, connectionId };
    Logger.log('sendCandidate:' + data);
    return fetch(this.url('candidate'), { method: 'POST', headers: this.headers(), body: JSON.stringify(data) });
    
  }

  private async getAll(fromTime: number = 0): Promise<Response> {
    return await fetch(this.url('', `fromtime=${fromTime}`), { method: 'GET', headers: this.headers() });
  }
}
